import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import type { RouteViewType } from '@/types';

import { INCIDENT_DETAILS, INCIDENTS_LIST, ROUTE_DETAILS, ROUTE_LIST } from '@/utils/constants';
import {
  SEARCH_PARAM_DRAWER,
  SEARCH_PARAM_INCIDENT_ID,
  SEARCH_PARAM_INCIDENT_SEARCH,
  SEARCH_PARAM_INCIDENTS,
  SEARCH_PARAM_JOURNEY_ID,
  SEARCH_PARAM_LOCK_PIN,
  SEARCH_PARAM_LOCK_PIN_TYPE,
  SEARCH_PARAM_ROUTE_LINE_NUMBER,
  SEARCH_PARAM_ROUTES,
  SEARCH_PARAM_SECTION_ID,
  SEARCH_PARAM_SHOW,
  SEARCH_PARAM_VIEW,
  SEARCH_PARAMS_ROUTE_ID,
  VIEW_LADDER,
} from '@/utils/constants/queries';

const useRoutesState = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const paramRouteId = searchParams.get(SEARCH_PARAMS_ROUTE_ID);
  const pinnedItemId = searchParams.get(SEARCH_PARAM_LOCK_PIN) ?? '';

  const paramIncidentId = searchParams.get(SEARCH_PARAM_INCIDENT_ID);
  const isIncidentsView = searchParams.get(SEARCH_PARAM_INCIDENTS) === 'true';

  const isUrlOpen = searchParams.get(SEARCH_PARAM_SHOW) === SEARCH_PARAM_ROUTES;
  const isLadderView = searchParams.get(SEARCH_PARAM_VIEW) === VIEW_LADDER;
  const paramSectionId = searchParams.get(SEARCH_PARAM_SECTION_ID) || '';
  const paramLockId = searchParams.get(SEARCH_PARAM_LOCK_PIN);
  const isLockPinned = !!paramLockId && !!paramRouteId;
  const lockType = searchParams?.get(SEARCH_PARAM_LOCK_PIN_TYPE);
  const paramRouteLineNumber = searchParams.get(SEARCH_PARAM_ROUTE_LINE_NUMBER);
  const paramJourneyId = searchParams.get(SEARCH_PARAM_JOURNEY_ID);

  const currentTab = searchParams.get(SEARCH_PARAM_SHOW);
  const isRoutesTabActive = currentTab === SEARCH_PARAM_ROUTES;

  const getViewType = (): RouteViewType => {
    const hasRouteId = !!paramRouteId;

    const shouldUsePinnedItem = currentTab
      ? lockType === 'route' && isRoutesTabActive
      : lockType === 'route';

    const hasPinnedItemWithClosedPanel = !!pinnedItemId && !isUrlOpen && shouldUsePinnedItem;

    const shouldShowRouteDetails = hasRouteId || hasPinnedItemWithClosedPanel;

    if (paramIncidentId) {
      return INCIDENT_DETAILS;
    }

    if (isIncidentsView) {
      return INCIDENTS_LIST;
    }

    if (shouldShowRouteDetails) {
      return ROUTE_DETAILS;
    }

    return ROUTE_LIST;
  };

  const viewType = getViewType();
  const hasBackIcon = viewType !== ROUTE_LIST;

  const handleClose = useCallback(() => {
    const updatedParams = new URLSearchParams();

    if (isLadderView) {
      updatedParams.set(SEARCH_PARAM_VIEW, VIEW_LADDER);
      updatedParams.set(SEARCH_PARAM_SECTION_ID, paramSectionId);
    }

    if (pinnedItemId) {
      updatedParams.set(SEARCH_PARAM_LOCK_PIN, pinnedItemId);
      updatedParams.set(SEARCH_PARAM_DRAWER, 'true');
    }

    if (lockType) {
      updatedParams.set(SEARCH_PARAM_LOCK_PIN_TYPE, lockType);
      updatedParams.set(SEARCH_PARAM_LOCK_PIN, pinnedItemId);
    }

    updatedParams.delete(SEARCH_PARAM_JOURNEY_ID);

    setSearchParams(updatedParams);
  }, [pinnedItemId, isLadderView, lockType, paramSectionId]);

  const handleBack = useCallback(() => {
    const currentIncidentSearch = searchParams.get(SEARCH_PARAM_INCIDENT_SEARCH);

    setSearchParams((prev) => {
      if (viewType === INCIDENT_DETAILS) {
        prev.delete(SEARCH_PARAM_INCIDENT_ID);
      } else if (viewType === INCIDENTS_LIST) {
        prev.delete(SEARCH_PARAM_INCIDENTS);

        if (currentIncidentSearch) {
          prev.set(SEARCH_PARAM_INCIDENT_SEARCH, currentIncidentSearch);
        } else {
          prev.delete(SEARCH_PARAM_INCIDENT_SEARCH);
        }
      } else if (viewType === ROUTE_DETAILS) {
        prev.delete(SEARCH_PARAMS_ROUTE_ID);
        prev.delete(SEARCH_PARAM_JOURNEY_ID);
        prev.delete(SEARCH_PARAM_ROUTE_LINE_NUMBER);
      }

      if (isLockPinned) {
        prev.set(SEARCH_PARAM_LOCK_PIN, pinnedItemId);
        prev.set(SEARCH_PARAM_LOCK_PIN_TYPE, 'route');
      }

      return prev;
    });
  }, [viewType, isLockPinned, paramLockId, pinnedItemId, searchParams]);

  const headerText = useMemo(() => {
    if (viewType === ROUTE_LIST) {
      return t('header.routes');
    }

    if (viewType === ROUTE_DETAILS) {
      return t('header.route_details');
    }

    if (viewType === INCIDENTS_LIST) {
      return `${t('header.incidents')} (${paramRouteLineNumber} ${paramJourneyId})`;
    }

    if (viewType === INCIDENT_DETAILS) {
      return t('header.incident_details');
    }

    return '';
  }, [viewType, paramRouteLineNumber, paramJourneyId, t]);

  const viewsWithBorder = [ROUTE_DETAILS, INCIDENT_DETAILS, INCIDENTS_LIST];
  const hasHeaderBottomBorder = viewsWithBorder.includes(viewType);

  return {
    viewType,
    headerText,
    hasHeaderBottomBorder,
    isShowSidePanel: isUrlOpen,
    hasBackIcon,
    handleClose,
    handleBack,
    paramRouteLineNumber,
    paramJourneyId,
  };
};

export default useRoutesState;
